import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from '@mui/material';
import AddNewSlotDialog from './AddNewSlotDialog';
import { formatLocalYmd } from './formatLocalYmd';
import { getTodayYmdPst, isDateBeforeEarliestManagerNewTargetPst } from './managerTargetWindow';

const HOUR_HEIGHT = 48;
const START_HOUR = 8;
// Show day view timeline from 8:00 to 22:00 (10pm)
const END_HOUR = 22;
const HOURS = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => i + START_HOUR);
const TOTAL_HEIGHT = HOUR_HEIGHT * (END_HOUR - START_HOUR);

const formatTime = (t) => {
  if (!t) return '00:00';
  const s = String(t).trim();
  if (s.length >= 5) return s.slice(0, 5);
  return s;
};

const formatRruleLabel = (rrule) => {
  if (!rrule) return '';
  const u = String(rrule).toUpperCase();
  if (u === 'DAILY') return 'Daily';
  if (u === 'WEEKLY') return 'Weekly';
  if (u === 'BIWEEKLY') return 'Biweekly';
  if (u === 'MONTHLY') return 'Monthly';
  return String(rrule);
};

const parseTimeToMinutes = (t) => {
  if (!t) return 0;
  const s = String(t).trim();
  const parts = s.split(':');
  const h = parseInt(parts[0] || 0, 10);
  const m = parseInt(parts[1] || 0, 10);
  return h * 60 + m;
};

const getEventStartMinutes = (ev) => {
  const s = ev.start;
  if (!s) return 0;
  if (typeof s === 'string') {
    const hhmm = s.length >= 16 ? s.slice(11, 16) : s.slice(0, 5);
    return parseTimeToMinutes(hhmm);
  }
  if (s instanceof Date) {
    const h = s.getHours();
    const m = s.getMinutes();
    return h * 60 + m;
  }
  return 0;
};
const getEventEndMinutes = (ev) => {
  if (ev.end) {
    const e = ev.end;
    if (typeof e === 'string') {
      const hhmm = e.length >= 16 ? e.slice(11, 16) : e.slice(0, 5);
      return parseTimeToMinutes(hhmm);
    }
    if (e instanceof Date) {
      const h = e.getHours();
      const m = e.getMinutes();
      return h * 60 + m;
    }
  }
  const et = ev.extendedProps?.endTime;
  if (et) return parseTimeToMinutes(et);
  return getEventStartMinutes(ev) + 60;
};

const roundTo30 = (minutes) => Math.floor(minutes / 30) * 30;
const minutesToTop = (mins) => ((mins - START_HOUR * 60) / 60) * HOUR_HEIGHT;

/**
 * Day view - 左右分栏：左边 Instructor，右边 Manager
 * 避免同时间 slot 打架
 */
const ProposalDayView = ({
  open,
  onClose,
  date,
  events,
  isManagerMode,
  isInstructorMode = false,
  onSlotClick,
  onAddSlot,
  instructorId,
  managerId,
  token,
  readOnly = false,
}) => {
  const { instructorEvents, managerEvents } = useMemo(() => {
    if (!date || !events) return { instructorEvents: [], managerEvents: [] };
    const dateStr =
      typeof date === 'string' ? date.slice(0, 10) : formatLocalYmd(date);
    const dayEvs = events.filter((e) => {
      if (e.extendedProps?.isShadow) return false;
      const s = e.start;
      if (!s) return false;
      let dStr;
      if (typeof s === 'string') {
        dStr = s.slice(0, 10);
      } else if (s instanceof Date) {
        dStr = formatLocalYmd(s);
      } else {
        return false;
      }
      return dStr === dateStr;
    });
    const instructor = dayEvs.filter((e) => (e.extendedProps?.sourceType || 'INSTRUCTOR') === 'INSTRUCTOR');
    const manager = dayEvs.filter((e) => e.extendedProps?.sourceType === 'MANAGER');
    const scheduleFromManager = dayEvs.filter((e) => e.extendedProps?.isSchedule && e.extendedProps?.sourceType === 'MANAGER');
    const scheduleFromInstructor = dayEvs.filter((e) => e.extendedProps?.isSchedule && e.extendedProps?.sourceType === 'INSTRUCTOR');
    const rejected = dayEvs.filter((e) => e.extendedProps?.isRejected);

    const instructorEvents = [...instructor, ...scheduleFromManager];
    const managerEvents = [...manager, ...scheduleFromInstructor];

    const hasById = (list, ev) => list.some((x) => x.id === ev.id);

    rejected.forEach((ev) => {
      if (!hasById(instructorEvents, ev)) {
        instructorEvents.push(ev);
      }
      if (!hasById(managerEvents, ev)) {
        managerEvents.push(ev);
      }
    });

    return {
      instructorEvents,
      managerEvents,
    };
  }, [date, events]);

  const getEventStyle = (ev) => {
    const start = getEventStartMinutes(ev);
    const end = getEventEndMinutes(ev);
    const top = minutesToTop(start);
    const height = Math.max(((end - start) / 60) * HOUR_HEIGHT, 24);
    let bgColor;
    if (ev.extendedProps?.isSchedule) {
      bgColor = 'rgba(76, 175, 80, 0.5)';
    } else if (ev.extendedProps?.isRejected) {
      bgColor = 'rgba(244, 67, 54, 0.5)';
    } else {
      const st = ev.extendedProps?.sourceType || 'INSTRUCTOR';
      bgColor = st === 'MANAGER' ? 'rgba(33, 150, 243, 0.5)' : 'rgba(255, 193, 7, 0.5)';
    }
    return { top, height, backgroundColor: bgColor };
  };

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addDialogManagerMode, setAddDialogManagerMode] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const [hoverMinutes, setHoverMinutes] = useState(null);
  const [prefillTimes, setPrefillTimes] = useState(null);
  const scrollContainerRef = useRef(null);
  /** Same box as time-axis ticks + both columns; used for hover/drag Y so guide lines align with ticks */
  const gridAreaRef = useRef(null);

  useEffect(() => {
    if (open && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [open]);

  const dateStrYmd = useMemo(() => {
    if (!date) return '';
    return typeof date === 'string' ? date.slice(0, 10) : formatLocalYmd(date);
  }, [date]);

  const managerNewTargetBlocked = isManagerMode && isDateBeforeEarliestManagerNewTargetPst(dateStrYmd);

  const isViewingTodayPst = useMemo(
    () => Boolean(dateStrYmd && dateStrYmd === getTodayYmdPst()),
    [dateStrYmd],
  );

  const canAddInstructor = !readOnly && onAddSlot && !isManagerMode;
  const canAddManager = !readOnly && onAddSlot && isManagerMode && !managerNewTargetBlocked;

  const existingSlotsForAddDialog = useMemo(() => {
    const sideEvents = addDialogManagerMode ? managerEvents : instructorEvents;
    return sideEvents.map((ev) => ({
      startMinutes: getEventStartMinutes(ev),
      endMinutes: getEventEndMinutes(ev),
    }));
  }, [addDialogManagerMode, instructorEvents, managerEvents]);

  const dragColumnRef = useRef(null);
  const dragValuesRef = useRef({ start: null, end: null });
  const dragCompletedRef = useRef(false);

  const minutesFromGridClientY = (clientY) => {
    const grid = gridAreaRef.current;
    if (!grid) return null;
    const rect = grid.getBoundingClientRect();
    const y = clientY - rect.top;
    const mins = START_HOUR * 60 + (y / HOUR_HEIGHT) * 60;
    return Math.max(START_HOUR * 60, Math.min(END_HOUR * 60, mins));
  };

  const handleGridMouseMove = (e) => {
    const mins = minutesFromGridClientY(e.clientY);
    if (mins == null) return;
    const snapped = roundTo30(mins);
    setHoverMinutes(snapped);
  };

  const handleDragStart = (e, isManagerColumn) => {
    if (!(isManagerColumn ? canAddManager : canAddInstructor)) return;
    const minsRaw = minutesFromGridClientY(e.clientY);
    if (minsRaw == null) return;
    const rounded = roundTo30(Math.max(START_HOUR * 60, Math.min(END_HOUR * 60 - 30, minsRaw)));
    dragValuesRef.current = { start: rounded, end: rounded };
    setDragStart(rounded);
    setDragEnd(rounded);
    dragColumnRef.current = { isManagerColumn, gridEl: gridAreaRef.current };
  };

  useEffect(() => {
    if (dragStart == null) return;
    const onMove = (e) => {
      const mins = minutesFromGridClientY(e.clientY);
      if (mins == null) return;
      const rounded = roundTo30(mins);
      dragValuesRef.current.end = rounded;
      setDragEnd(rounded);
    };
    const onUp = () => {
      const col = dragColumnRef.current;
      const { start: s, end: e } = dragValuesRef.current;
      dragColumnRef.current = null;
      setDragStart(null);
      setDragEnd(null);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (col && s != null && e != null) {
        const start = Math.min(s, e);
        let end = Math.max(s, e);
        if (end - start < 30) return;
        if (end <= END_HOUR * 60) {
          const sh = Math.floor(start / 60);
          const sm = start % 60;
          const eh = Math.floor(end / 60);
          const em = end % 60;
          dragCompletedRef.current = true;
          setPrefillTimes({
            start: `${String(sh).padStart(2, '0')}:${String(sm).padStart(2, '0')}`,
            end: `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`,
          });
          setAddDialogManagerMode(col.isManagerColumn);
          setAddDialogOpen(true);
        }
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [dragStart]);

  const renderColumn = (evs, isManagerColumn) => {
    const canAdd = isManagerColumn ? canAddManager : canAddInstructor;
    const managerColumnReadOnlyBg = isManagerColumn && managerNewTargetBlocked;
    const managerTodayHighlight =
      isManagerColumn && managerColumnReadOnlyBg && isViewingTodayPst;
    const isDragging = dragStart != null && dragEnd != null;
    const dragTop = isDragging ? minutesToTop(Math.min(dragStart, dragEnd)) : 0;
    const dragHeight = isDragging ? Math.max((30 / 60) * HOUR_HEIGHT, (Math.abs(dragEnd - dragStart) / 60) * HOUR_HEIGHT) : 0;
    return (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => {
        if (e.target !== e.currentTarget && !e.target.closest('[data-slot-event]')) return;
        if (!canAdd) return;
        if (dragCompletedRef.current) {
          dragCompletedRef.current = false;
          return;
        }
        setAddDialogManagerMode(isManagerColumn);
        setPrefillTimes(null);
        setAddDialogOpen(true);
      }}
      onMouseDown={(e) => { if (canAdd && !e.target.closest('[data-slot-event]')) handleDragStart(e, isManagerColumn); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (canAdd) {
            setAddDialogManagerMode(isManagerColumn);
            setAddDialogOpen(true);
          }
        }
      }}
      style={{
        position: 'relative',
        boxSizing: 'border-box',
        height: TOTAL_HEIGHT,
        minHeight: TOTAL_HEIGHT,
        cursor: canAdd ? 'crosshair' : 'default',
        backgroundColor: canAdd
          ? '#e0e0e0'
          : managerTodayHighlight
            ? 'rgba(255, 243, 224, 0.95)'
            : managerColumnReadOnlyBg
              ? 'rgba(0, 0, 0, 0.06)'
              : undefined,
        boxShadow: managerTodayHighlight ? 'inset 0 0 0 2px rgba(25, 118, 210, 0.45)' : undefined,
        overflow: 'hidden',
      }}
    >
      {isDragging && canAdd && (
        <div
          style={{
            position: 'absolute',
            left: 4,
            right: 4,
            top: dragTop,
            height: dragHeight,
            boxSizing: 'border-box',
            backgroundColor: 'rgba(33, 150, 243, 0.3)',
            borderRadius: 4,
            pointerEvents: 'none',
          }}
        />
      )}
      {evs.map((ev) => (
        <div
          key={ev.id}
          data-slot-event
          onClick={(e) => {
            e.stopPropagation();
            onSlotClick && onSlotClick(ev);
          }}
          style={{
            position: 'absolute',
            left: 4,
            right: 4,
            boxSizing: 'border-box',
            cursor: 'pointer',
            borderRadius: 4,
            padding: '4px 8px',
            overflow: 'hidden',
            fontSize: 12,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            textAlign: 'left',
            ...getEventStyle(ev),
          }}
        >
          {ev.extendedProps?.creatorName && (
            <div style={{ fontSize: 10, opacity: 0.9 }}>By: {ev.extendedProps.creatorName}</div>
          )}
          <div style={{ fontWeight: 700, color: '#000', fontSize: 13 }}>
            {(() => {
              const s = ev.start;
              let startLabel = '00:00';
              if (typeof s === 'string') {
                startLabel = s.length >= 16 ? s.slice(11, 16) : s.slice(0, 5);
              } else if (s instanceof Date) {
                const h = String(s.getHours()).padStart(2, '0');
                const m = String(s.getMinutes()).padStart(2, '0');
                startLabel = `${h}:${m}`;
              }
              return `${startLabel} - ${formatTime(ev.extendedProps?.endTime)}`;
            })()}
          </div>
          <div>{ev.title}</div>
          {ev.extendedProps?.isRecurring && ev.extendedProps?.rrule && (
            <div style={{ fontSize: 10, opacity: 0.85, marginTop: 2 }}>
              Recurring: {formatRruleLabel(ev.extendedProps.rrule)}
            </div>
          )}
        </div>
      ))}
    </div>
    );
  };

  const dateLabel = date ? (typeof date === 'string' ? date.slice(0, 10) : formatLocalYmd(date)) : '';

  const showInstructorInstructions = Boolean(isInstructorMode);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Day View - {dateLabel}</DialogTitle>
      <DialogContent ref={scrollContainerRef} sx={{ p: 0, minHeight: 400, maxHeight: 560, overflow: 'auto' }}>
        {showInstructorInstructions && (
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee', bgcolor: 'grey.50' }}>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              To select your availability, please check the target time proposed by the manager (on the right). Click <strong>CONFIRM</strong> if you can make it, and click <strong>REJECT</strong> if you can&apos;t. After rejection, you can propose your availability on the left half by dragging or clicking the gray area to type your preferred time slot on that day.
            </Typography>
          </Box>
        )}
        {isManagerMode && managerNewTargetBlocked && (
          <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid #eee', bgcolor: 'action.hover' }}>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              This date is before <strong>today (PST) + 7 days</strong>. You cannot add a new manager target here; existing slots can still be opened. Switch to a later day in month view to create targets.
            </Typography>
          </Box>
        )}
        <div style={{ display: 'flex', padding: '8px 0', borderBottom: '1px solid #eee', fontSize: 12 }}>
          <div style={{ width: 48, flexShrink: 0, borderRight: '1px solid #eee' }} />
          <div style={{ display: 'flex', flex: 1, borderLeft: '1px solid #eee', gap: 0 }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, paddingLeft: 4 }}>
              <span style={{ width: 14, height: 14, backgroundColor: 'rgba(255, 193, 7, 0.5)', borderRadius: 2 }} />
              Instructor (left)
            </div>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, paddingLeft: 4, borderLeft: '1px solid #ddd' }}>
              <span style={{ width: 14, height: 14, backgroundColor: 'rgba(33, 150, 243, 0.5)', borderRadius: 2 }} />
              Manager (right)
            </div>
          </div>
        </div>
        <div
          ref={gridAreaRef}
          style={{
            position: 'relative',
            display: 'grid',
            gridTemplateColumns: `${48}px 1fr 1fr`,
            gridTemplateRows: `${TOTAL_HEIGHT}px`,
            minHeight: TOTAL_HEIGHT,
            height: TOTAL_HEIGHT,
            boxSizing: 'border-box',
            columnGap: 0,
            rowGap: 0,
            alignItems: 'stretch',
          }}
          onMouseMove={handleGridMouseMove}
          onMouseLeave={() => setHoverMinutes(null)}
        >
          <div style={{ borderRight: '2px solid #000', boxSizing: 'border-box', minHeight: 0, overflow: 'hidden' }}>
            {HOURS.map((h) => (
              <div
                key={h}
                style={{
                  position: 'relative',
                  boxSizing: 'border-box',
                  height: HOUR_HEIGHT,
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#000',
                  paddingLeft: 4,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  lineHeight: 1,
                }}
              >
                {h === 0 ? '12a' : h < 12 ? `${h}a` : h === 12 ? '12p' : `${h - 12}p`}
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: 16,
                    borderTop: '1px solid #000',
                    opacity: 0.8,
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: HOUR_HEIGHT / 2,
                    right: 0,
                    width: 10,
                    borderTop: '1px solid #000',
                    opacity: 0.9,
                  }}
                />
              </div>
            ))}
          </div>
          <div
            style={{
              position: 'relative',
              boxSizing: 'border-box',
              minHeight: 0,
              borderLeft: '1px solid #eee',
              borderRight: '1px solid #ddd',
            }}
          >
            {renderColumn(instructorEvents, false)}
          </div>
          <div style={{ position: 'relative', boxSizing: 'border-box', minHeight: 0 }}>
            {renderColumn(managerEvents, true)}
          </div>
          {hoverMinutes != null && (
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: minutesToTop(hoverMinutes),
                height: 0,
                borderTop: '1px dashed rgba(0, 0, 0, 0.8)',
                pointerEvents: 'none',
                zIndex: 9998,
              }}
            />
          )}
          {dragStart != null && dragEnd != null && (
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: minutesToTop(Math.min(dragStart, dragEnd)),
                height: 0,
                borderTop: '2px solid rgba(25, 118, 210, 1)',
                pointerEvents: 'none',
                zIndex: 9999,
              }}
            />
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
      {onAddSlot && (
        <AddNewSlotDialog
          key={addDialogOpen && prefillTimes ? `prefill-${prefillTimes.start}-${prefillTimes.end}` : 'add-slot'}
          open={addDialogOpen}
          onClose={() => { setAddDialogOpen(false); setPrefillTimes(null); }}
          date={date}
          isManagerMode={addDialogManagerMode}
          onSave={onAddSlot}
          instructorId={instructorId}
          managerId={managerId}
          token={token}
          prefillTimes={prefillTimes}
          existingSlots={existingSlotsForAddDialog}
        />
      )}
    </Dialog>
  );
};

export default ProposalDayView;
